import React, { ReactNode } from 'react';

import { A11YLiveMessage } from '@lumapps/aria-live/components/A11YLiveMessage';
import { padding, useClassnames } from '@lumapps/classnames';
import { Size } from '@lumapps/lumx/react';

import { ChatMessageGroup } from '../ChatMessageGroup';
import { ChatMessageLoading } from '../ChatMessageLoading';

import './index.scss';

export interface ChatThreadProps {
    /** Children to display in the Thread. Meant to be a succession of `Chat.Message` */
    children: ReactNode;
    /** className to forward */
    className?: string;
    /** Whether next answer to receive is loading. Will display a `ChatMessageLoading` when true. */
    isLoadingMessage?: boolean;
}

const CLASSNAME = 'lumx-chat-thread';

/**
 * A chat container that manages and displays messages in an accessible live region.
 * Shows a placeholder when waiting for a response.
 * @family Chat
 * @param ChatThreadProps
 * @returns ChatThread
 */
export const ChatThread: React.FC<ChatThreadProps> = ({ children, className, isLoadingMessage = false }) => {
    const { block, element } = useClassnames(CLASSNAME);

    return (
        <A11YLiveMessage className={block([padding('all', Size.big), className])}>
            <ChatMessageGroup className={element('messages')}>
                {children}
                {isLoadingMessage && <ChatMessageLoading />}
            </ChatMessageGroup>
        </A11YLiveMessage>
    );
};
